import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { decode } from 'jsonwebtoken';
import isEmpty from 'lodash.isempty';
import { Helmet } from 'react-helmet-async';
import {
  authenticateUser,
  postPasswordForgottenRequest,
  refreshTokens,
} from '../store/actions/user/userActions';
import { PAGES } from '../constants/pages';
import {
  FormContainer,
  FormWrap,
} from '../components/FormContainer/FormContainer';
import BaseLayoutCta from '../components/BaseLayoutCta/BaseLayoutCta';
import Paragraph from '../components/Paragraph/Paragraph';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import {
  EMAIL,
  LANDING_PAGE_URL,
  OFFER_CODE,
  REGISTRATION_FLOW_TYPE,
  SIGNIFYD_SESSION_ID,
} from '../constants/sessionStorage';
import useGtmHook from '../util/hooks/useGtmHook';
import {
  ACCOUNT_STEP_COMPLETE,
  ACCOUNT_STEP_VIEW,
  APPLICATION_STARTED,
} from '../util/gtmEvents';
import {
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CREATE_NEW_APPLICATION,
  REGISTRATION_FLOWS,
  REGISTRATION_FLOW_TYPES,
} from '../util/constants';
import BaseLayout from '../components/BaseLayout';
import { Heading } from '../components/Heading';
import PasswordInput from '../components/Fields/PasswordInput';
import Select from '../components/Fields/Select';
import { getValidationSchema } from '../util/helpers/getValidationSchema';
import {
  selectRegistrationUrl,
  selectSettings,
} from '../store/selectors/settingsSelectors';
import ErrorModal from '../components/Modals/ErrorModal/ErrorModal';
import themeColors from '../assets/styles/themeColors';
import { JWT_TOKEN } from '../constants/localStorage';
import {
  createApplication,
  getApplication,
} from '../store/actions/application/applicationActions';
import { selectApplication } from '../store/selectors/applicationSelectors';
import ExistingApplicationModal from '../components/Modals/ExistingApplicationModal/ExistingApplicationModal';
import loadSignifydDeviceFingerprintScript from '../signifydDeviceFingerprint';
import { getUsernames } from '../util/helpers/userHelpers';
import { getTokens } from '../util/helpers/getTokensHelper';
import { APPLICATION_TYPE_INDIVIDUAL } from '../constants/accountTypes';
import { gtmStepHelper } from '../util/helpers/gtmHelper';

const LoginLink = styled.a`
  color: ${themeColors.colorTextSecondary};
  text-decoration: none;
  font-weight: 600;
`;

const AdditionalCTAContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.AlignRight ? 'flex-end' : 'space-between'};
`;

const Login = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [Fp, setFp] = useState('');
  const ReferrerUrl = document.referrer;
  const { gtmEvent, gtmStepEvent } = useGtmHook();
  const usernames = getUsernames();
  const [errorMsg, setErrorMsg] = useState();
  const [isInvalidEmailModalOpen, setIsInvalidEmailModalOpen] = useState(false);
  const [isExistingModalOpen, setIsExistingModalOpen] = useState(false);
  const Application = useSelector(selectApplication);
  const RegistrationUrl = useSelector(selectRegistrationUrl);
  const settings = useSelector(selectSettings);
  const { BrokerName } = settings;
  const registrationFlowType = retrieveFromSessionStorage(
    REGISTRATION_FLOW_TYPE,
  );
  const { JwtToken, JwtRefreshToken } = getTokens();
  const isAipFlow =
    retrieveFromSessionStorage(REGISTRATION_FLOW_TYPE) ===
    REGISTRATION_FLOW_TYPES.AIP;
  const formValidationSchema = yup.object({
    ...getValidationSchema({
      fields: ['Username'],
    }),
  });
  const { handleSubmit, setError, watch, control } = useForm({
    defaultValues: {
      Username: usernames[0],
    },
    resolver: yupResolver(formValidationSchema),
  });

  const username = watch('Username');

  useEffect(() => {
    gtmStepEvent(ACCOUNT_STEP_VIEW, 'Login');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user refreshes page
  useEffect(() => {
    function checkUsers() {
      if (!isEmpty(usernames)) {
        return;
      }

      history.replace(PAGES.BASE);
    }

    checkUsers();
  }, [dispatch, history, usernames]);

  useEffect(() => {
    setTimeout(() => {
      window.Fingerprint2?.get((components) => {
        const values = components.map((component) => component.value);
        const fingerPrint = window.Fingerprint2.x64hash128(values.join(''), 31);
        setFp(fingerPrint);
      });
    });
  }, []);

  useEffect(() => {
    const {
      ApplicationStep,
      AccountUid,
      RegistrationFlowType,
      ApplicationType,
      AccountUpgradeType,
    } = Application;

    if (isEmpty(Application)) {
      return;
    }
    const isUpgrade =
      AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;
    if (
      (ApplicationStep &&
        ApplicationStep === '/kyc/verification' &&
        isUpgrade) ||
      ApplicationStep === '/kyc/done'
    ) {
      history.push(PAGES.CHOOSE_APPLICATION.MAIN);
    }
    if (
      ApplicationStep ||
      (ApplicationType &&
        (!AccountUpgradeType ||
          AccountUpgradeType !== BASIC_ACCOUNT_UPGRADE_TYPES.UPGRADED))
    ) {
      setIsExistingModalOpen(true);
      return;
    }
    if (AccountUid) {
      history.push(PAGES.LOADING);
    }

    if (
      registrationFlowType !== REGISTRATION_FLOWS[RegistrationFlowType] &&
      ApplicationType
    ) {
      setIsExistingModalOpen(true);
      return;
    }
    history.push(PAGES.LOADING);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application, history, registrationFlowType]);

  const onSuccess = () => {
    gtmEvent(APPLICATION_STARTED);

    const JwtToken = retrieveFromSessionStorage(JWT_TOKEN);
    if (!JwtToken) {
      return;
    }

    const { ApplicationUid } = decode(JwtToken);

    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Login',
    });

    dispatch(getApplication({ data: { ApplicationUid } }));
  };

  const onForgotPasswordError = (errorMesage) => {
    setErrorMsg(errorMesage);
    setIsInvalidEmailModalOpen(true);
  };

  const redirectToV1 = (email) => {
    window.location.href = `${RegistrationUrl}/r/start?email=${encodeURIComponent(
      email,
    )}`;
  };

  const onSubmit = (data) => {
    const onError = (errorMessage) => {
      setError('Username', errorMessage);
    };

    const Offer = retrieveFromSessionStorage(OFFER_CODE);
    const LandingPageUrl = retrieveFromSessionStorage(LANDING_PAGE_URL);

    dispatch(
      authenticateUser({
        data: {
          Email: data?.Username?.value,
          Password: data?.Password,
          TrackingDetails: {
            Fp,
            LandingPageUrl,
            Offer,
            ReferrerUrl,
          },
        },
        onSuccess,
        onError,
        redirectToV1,
      }),
    );
  };

  const sendReset = () => {
    if (!username) {
      setErrorMsg(t('resetPassword.emptyEmail'));
      setIsInvalidEmailModalOpen(true);
      return;
    }
    dispatch(
      postPasswordForgottenRequest({
        data: {
          Username: username?.value,
        },
        handleSuccess: () => {
          history.push(PAGES.RESET_SENT);
        },
        handleError: onForgotPasswordError,
      }),
    );
  };
  const continueApplication = () => {
    setIsExistingModalOpen(false);
    history.push(PAGES.LOADING);
    document.body.style.overflow = 'unset';

    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);

    // eslint-disable-next-line no-console
    console.log(
      'NEW CREATED SIGNIFYD_SESSION_ID:',
      script.dataset.orderSessionId,
    );
  };
  const startNewApplication = () => {
    const flowType = retrieveFromSessionStorage(REGISTRATION_FLOW_TYPE);
    if (flowType !== 'Aip') {
      storeInSessionStorage(CREATE_NEW_APPLICATION, true);
      history.push(PAGES.CHOOSE_APPLICATION.MAIN);
      return;
    }
    const onSuccess = () => {
      dispatch(
        refreshTokens({
          data: {
            JwtToken,
            JwtRefreshToken,
          },
          onSuccess: () => {
            history.push(PAGES.LOADING);
            removeFromSessionStorage(CREATE_NEW_APPLICATION);
            document.body.style.overflow = 'unset';
          },
        }),
      );
    };
    const { ApplicationUid } = decode(JwtToken);
    const params = {
      registrationFlowType: isAipFlow
        ? REGISTRATION_FLOW_TYPES.AIP
        : REGISTRATION_FLOW_TYPES.REGULAR,
    };
    dispatch(
      createApplication({
        data: { ApplicationType: APPLICATION_TYPE_INDIVIDUAL },
        onSuccess,
        ApplicationUid,
        params,
      }),
    );
  };
  return (
    <BaseLayout noLogoBox previousPath={isAipFlow ? PAGES.AIP : PAGES.BASE}>
      <Helmet>
        <title>
          {t('loginFluent.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading fontWeight={700} mb={28}>
        {t('loginFluent.title')}
      </Heading>
      <FormWrap>
        <FormContainer maxWidth={344}>
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <Select
                label={t('loginFluent.email')}
                options={usernames}
                {...field}
                {...fieldState}
                autoFocus
                isSearchable={false}
              />
            )}
            name="Username"
            defaultValue=""
          />
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <PasswordInput
                label={t('loginFluent.password')}
                {...field}
                {...fieldState}
              />
            )}
            name="Password"
            defaultValue=""
          />
          <AdditionalCTAContainer alignRight>
            <LoginLink
              onClick={(e) => {
                e.preventDefault();
                sendReset();
              }}
            >
              {t('loginFluent.forgotPassword')}
            </LoginLink>
          </AdditionalCTAContainer>
        </FormContainer>
        <BaseLayoutCta
          buttonProps={{
            type: 'submit',
            title: t('loginFluent.login'),
            onClick: handleSubmit(onSubmit),
          }}
          content={
            <Paragraph>
              {t('loginFluent.application')}{' '}
              <LoginLink
                onClick={(e) => {
                  e.preventDefault();

                  if (isAipFlow) {
                    history.push(PAGES.AIP);
                  } else {
                    removeFromSessionStorage(EMAIL);
                    history.push(PAGES.CREATE_ACCOUNT);
                  }
                }}
              >
                {t('loginFluent.getStarted')}
              </LoginLink>
            </Paragraph>
          }
        />
      </FormWrap>
      <ErrorModal
        isOpen={isInvalidEmailModalOpen}
        onClose={() => setIsInvalidEmailModalOpen(false)}
        errorMessage={errorMsg}
        redirect={() => setIsInvalidEmailModalOpen(false)}
      />
      <ExistingApplicationModal
        isOpen={isExistingModalOpen}
        continueApplication={continueApplication}
        startNewApplication={startNewApplication}
      />
    </BaseLayout>
  );
};

export default Login;
